import React from 'react';
import { ChevronDown, ChevronUp, Package } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { fmtR, COST_TYPE_ICONS, COST_TYPE_BG } from './constants';

/**
 * Cost & Efficiency by Vehicle — table with expandable transaction rows.
 * Extracted from ReportsPage.jsx to keep the parent page under 500 lines.
 */
export const VehicleCostBreakdown = ({
  vb,
  monthLabel,
  expandedVehicle,
  toggleVehicleTxns,
  vehicleTxns,
  loadingTxns,
  fleetAvgKml,
  fleetAvgLph,
  fleetKm,
  fleetHrs,
  fleetL,
}) => (
  <Card className="card-hover">
    <CardHeader className="pb-2">
      <CardTitle className="text-lg font-heading font-semibold">
        Cost &amp; Efficiency by Vehicle — {monthLabel}
      </CardTitle>
    </CardHeader>
    <CardContent>
      {vb.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="vehicle-cost-table">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Vehicle</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">km/L · L/hr</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Total km · hrs</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Liters</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Fuel</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Service</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Other</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {vb.map(v => {
                const isExpanded = expandedVehicle === v.vehicle_id;
                return (
                  <React.Fragment key={v.vehicle_id}>
                    <tr
                      className={`border-b border-slate-100 cursor-pointer transition-colors ${isExpanded ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                      onClick={() => toggleVehicleTxns(v.vehicle_id)}
                      data-testid={`vehicle-row-${v.vehicle_id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
                          <div>
                            <p className="font-medium text-slate-900">{v.name}</p>
                            <p className="text-sm text-slate-500 font-data">{v.registration}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        {v.meter_type === 'hours' ? (
                          v.l_per_hour ? (
                            <span
                              className={`font-data font-bold ${v.l_per_hour <= 2 ? 'text-green-600' : v.l_per_hour <= 4 ? 'text-amber-600' : 'text-red-600'}`}
                              title="Liters per operating hour (lower = better)"
                            >
                              {v.l_per_hour.toFixed(2)} L/hr
                            </span>
                          ) : <span className="text-slate-400">-</span>
                        ) : v.km_per_liter ? (
                          <span className={`font-data font-bold ${v.km_per_liter >= 10 ? 'text-green-600' : v.km_per_liter >= 7 ? 'text-amber-600' : 'text-red-600'}`}>
                            {v.km_per_liter}
                          </span>
                        ) : <span className="text-slate-400">-</span>}
                      </td>
                      <td className="py-3 px-4 text-right font-data text-slate-600">
                        {v.meter_type === 'hours'
                          ? (v.total_hours ? `${v.total_hours.toLocaleString()} hrs` : '-')
                          : (v.total_km ? `${v.total_km.toLocaleString()}` : '-')}
                      </td>
                      <td className="py-3 px-4 text-right font-data text-slate-600">
                        {v.liters ? `${v.liters.toFixed(1)}` : '-'}
                      </td>
                      <td className="py-3 px-4 text-right font-data text-blue-600">{fmtR(v.fuel)}</td>
                      <td className="py-3 px-4 text-right font-data text-red-600">{fmtR(v.service)}</td>
                      <td className="py-3 px-4 text-right font-data text-orange-600">{fmtR((v.tyres || 0) + (v.maintenance || 0) + (v.other || 0))}</td>
                      <td className="py-3 px-4 text-right font-data font-bold text-slate-900">{fmtR(v.total)}</td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} className="p-0">
                          <div className="bg-slate-50 border-b border-slate-200 px-6 py-4" data-testid={`vehicle-txns-${v.vehicle_id}`}>
                            <p className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3">
                              Transactions for {v.name} — {monthLabel}
                            </p>
                            {loadingTxns ? (
                              <p className="text-sm text-slate-400 py-4 text-center">Loading transactions...</p>
                            ) : vehicleTxns.length === 0 ? (
                              <p className="text-sm text-slate-400 py-4 text-center">No transactions found</p>
                            ) : (
                              <TransactionTable vehicleTxns={vehicleTxns} meterType={v.meter_type} />
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-50">
                <td className="py-3 px-4 font-bold text-slate-900">Fleet Totals</td>
                <td className="py-3 px-4 text-right font-data font-bold" data-testid="fleet-total-efficiency">
                  {(fleetAvgKml || fleetAvgLph) ? (
                    <div className="flex flex-col items-end gap-0.5 leading-tight">
                      {fleetAvgKml && (
                        <span className={fleetAvgKml >= 10 ? 'text-green-600' : fleetAvgKml >= 7 ? 'text-amber-600' : 'text-red-600'}>
                          {fleetAvgKml.toFixed(2)} km/L
                        </span>
                      )}
                      {fleetAvgLph && (
                        <span className={fleetAvgLph <= 2 ? 'text-green-600' : fleetAvgLph <= 4 ? 'text-amber-600' : 'text-red-600'}>
                          {fleetAvgLph.toFixed(2)} L/hr
                        </span>
                      )}
                    </div>
                  ) : '-'}
                </td>
                <td className="py-3 px-4 text-right font-data font-bold text-slate-600" data-testid="fleet-total-meter">
                  {(fleetKm > 0 || fleetHrs > 0) ? (
                    <div className="flex flex-col items-end gap-0.5 leading-tight">
                      {fleetKm > 0 && <span>{fleetKm.toLocaleString()} km</span>}
                      {fleetHrs > 0 && <span>{fleetHrs.toLocaleString()} hrs</span>}
                    </div>
                  ) : '-'}
                </td>
                <td className="py-3 px-4 text-right font-data font-bold text-slate-600">
                  {fleetL.toFixed(1)}
                </td>
                <td className="py-3 px-4 text-right font-data font-bold text-blue-600">
                  {fmtR(vb.reduce((s, v) => s + (v.fuel || 0), 0))}
                </td>
                <td className="py-3 px-4 text-right font-data font-bold text-red-600">
                  {fmtR(vb.reduce((s, v) => s + (v.service || 0), 0))}
                </td>
                <td className="py-3 px-4 text-right font-data font-bold text-orange-600">
                  {fmtR(vb.reduce((s, v) => s + (v.tyres || 0) + (v.maintenance || 0) + (v.other || 0), 0))}
                </td>
                <td className="py-3 px-4 text-right font-data font-bold text-slate-900">
                  {fmtR(vb.reduce((s, v) => s + (v.total || 0), 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="h-[200px] flex items-center justify-center text-slate-400">
          No cost data for {monthLabel}
        </div>
      )}
    </CardContent>
  </Card>
);


const TransactionTable = ({ vehicleTxns, meterType }) => {
  const isHours = meterType === 'hours';
  const meterField = isHours ? 'hours' : 'odometer';
  const meterLabel = isHours ? 'Hours' : 'Odometer';
  const meterUnit = isHours ? 'hrs' : 'km';
  const efficiencyLabel = isHours ? 'L/hr' : 'km/L';

  // Pre-compute per-entry efficiency (km/L or L/hr) between consecutive fuel entries.
  const fuelSorted = vehicleTxns
    .filter(t => t.cost_type === 'fuel' && t[meterField] && t.liters)
    .sort((a, b) => a.date.localeCompare(b.date) || a[meterField] - b[meterField]);
  const effMap = {};
  for (let i = 1; i < fuelSorted.length; i++) {
    const delta = fuelSorted[i][meterField] - fuelSorted[i - 1][meterField];
    if (delta > 0 && fuelSorted[i].liters > 0) {
      const val = isHours
        ? fuelSorted[i].liters / delta   // L/hr — lower is better
        : delta / fuelSorted[i].liters;  // km/L — higher is better
      effMap[fuelSorted[i].id] = parseFloat(val.toFixed(2));
    }
  }
  const fuelAll = vehicleTxns.filter(t => t.cost_type === 'fuel');
  const readings = fuelAll.filter(t => t[meterField]).map(t => t[meterField]).sort((a, b) => a - b);
  const totalMeter = readings.length >= 2 ? readings[readings.length - 1] - readings[0] : 0;
  const totalLiters = fuelAll.reduce((s, t) => s + (t.liters || 0), 0);
  const avgEff = totalMeter > 0 && totalLiters > 0
    ? (isHours ? totalLiters / totalMeter : totalMeter / totalLiters)
    : null;
  const totalAmount = vehicleTxns.reduce((s, t) => s + (t.amount || 0), 0);

  // Colour thresholds differ between km/L (higher-is-better) and L/hr (lower-is-better).
  const effColour = (v) => {
    if (v == null) return 'text-slate-400';
    if (isHours) return v <= 2 ? 'text-green-600' : v <= 4 ? 'text-amber-600' : 'text-red-600';
    return v >= 10 ? 'text-green-600' : v >= 7 ? 'text-amber-600' : 'text-red-600';
  };

  return (
    <table className="w-full">
      <thead>
        <tr className="border-b border-slate-200">
          <th className="text-left py-2 px-3 text-xs uppercase text-slate-400 font-medium">Date</th>
          <th className="text-left py-2 px-3 text-xs uppercase text-slate-400 font-medium">Type</th>
          <th className="text-left py-2 px-3 text-xs uppercase text-slate-400 font-medium">Description</th>
          <th className="text-right py-2 px-3 text-xs uppercase text-slate-400 font-medium">{meterLabel}</th>
          <th className="text-right py-2 px-3 text-xs uppercase text-slate-400 font-medium">Liters</th>
          <th className="text-right py-2 px-3 text-xs uppercase text-slate-400 font-medium">{efficiencyLabel}</th>
          <th className="text-right py-2 px-3 text-xs uppercase text-slate-400 font-medium">Amount</th>
        </tr>
      </thead>
      <tbody>
        {vehicleTxns.map(txn => {
          const Icon = COST_TYPE_ICONS[txn.cost_type] || Package;
          const bg = COST_TYPE_BG[txn.cost_type] || 'bg-slate-100 text-slate-600';
          const entryEff = effMap[txn.id];
          const isFuel = txn.cost_type === 'fuel';
          const meterVal = txn[meterField];
          const isFirstFill = isFuel && meterVal && txn.liters && !entryEff && fuelSorted.length > 0 && fuelSorted[0].id === txn.id;
          return (
            <tr key={txn.id} className="border-b border-slate-100 hover:bg-white transition-colors" data-testid={`txn-${txn.id}`}>
              <td className="py-2.5 px-3 font-data text-sm text-slate-700">{txn.date}</td>
              <td className="py-2.5 px-3">
                <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-md ${bg}`}>
                  <Icon className="w-3 h-3" />
                  {txn.cost_type.charAt(0).toUpperCase() + txn.cost_type.slice(1)}
                </span>
              </td>
              <td className="py-2.5 px-3 text-sm text-slate-600">
                {txn.description || txn.supplier_name || '-'}
              </td>
              <td className="py-2.5 px-3 text-right font-data text-sm text-slate-500">
                {meterVal ? `${meterVal.toLocaleString()} ${meterUnit}` : '-'}
              </td>
              <td className="py-2.5 px-3 text-right font-data text-sm text-slate-500">
                {txn.liters ? `${txn.liters} L` : '-'}
              </td>
              <td className="py-2.5 px-3 text-right font-data text-sm">
                {entryEff ? (
                  <span className={`font-bold ${effColour(entryEff)}`}>
                    {entryEff}
                  </span>
                ) : isFirstFill ? (
                  <span className="text-slate-400 text-xs">1st fill</span>
                ) : isFuel ? (
                  <span className="text-slate-300">-</span>
                ) : ''}
              </td>
              <td className="py-2.5 px-3 text-right font-data text-sm font-bold text-slate-800">
                {fmtR(txn.amount)}
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr className="border-t border-slate-300 bg-slate-100/50">
          <td colSpan={3} className="py-3 px-3 text-xs uppercase text-slate-500 font-medium">
            Total ({vehicleTxns.length} entries)
          </td>
          <td className="py-3 px-3 text-right font-data font-bold text-slate-700" data-testid="txn-total-km">
            {totalMeter > 0 ? `${totalMeter.toLocaleString()} ${meterUnit}` : '-'}
          </td>
          <td className="py-3 px-3 text-right font-data font-bold text-slate-700" data-testid="txn-total-liters">
            {totalLiters > 0 ? `${totalLiters.toFixed(1)} L` : '-'}
          </td>
          <td className="py-3 px-3 text-right font-data font-bold" data-testid="txn-avg-kml">
            {avgEff ? (
              <span className={effColour(avgEff)}>
                {avgEff.toFixed(2)}
              </span>
            ) : '-'}
          </td>
          <td className="py-3 px-3 text-right font-data font-bold text-slate-900" data-testid="txn-total-amount">
            {fmtR(totalAmount)}
          </td>
        </tr>
      </tfoot>
    </table>
  );
};
